import React from 'react';
import { Link } from 'react-router-dom';
import LegalPage from './LegalPage';

const INK  = '#0A1F4A';
const INK2 = '#3D4F78';
const RULE = '#D6DAE4';
const ACC  = '#FF7A3D';

// TODO: update when company is incorporated
const PRIVACY_EMAIL = 'privacy@jyto.eu';
const LEGAL_EMAIL   = 'legal@jyto.eu';
const COMPANY_NAME  = 'Jyto B.V.';

const s = {
  lead:  { fontSize: 16, lineHeight: 1.75, color: INK2, margin: '0 0 40px', paddingBottom: 40, borderBottom: `1px solid ${RULE}` },
  h2:    { fontSize: 19, fontWeight: 700, color: INK, margin: '40px 0 12px', letterSpacing: '-0.02em', paddingTop: 40, borderTop: `1px solid ${RULE}` },
  h2top: { fontSize: 19, fontWeight: 700, color: INK, margin: '0 0 12px', letterSpacing: '-0.02em' },
  h3:    { fontSize: 15, fontWeight: 600, color: INK, margin: '24px 0 8px' },
  p:     { fontSize: 15, lineHeight: 1.75, color: INK2, margin: '0 0 16px' },
  ul:    { fontSize: 15, lineHeight: 1.75, color: INK2, paddingLeft: 24, margin: '0 0 16px' },
  li:    { marginBottom: 6 },
  table: { width: '100%', borderCollapse: 'collapse', fontSize: 13, margin: '16px 0 24px', display: 'block', overflowX: 'auto' },
  th:    { textAlign: 'left', padding: '8px 12px', background: '#F6F7FB', borderBottom: `1px solid ${RULE}`, color: INK, fontWeight: 600, fontSize: 12, whiteSpace: 'nowrap' },
  td:    { padding: '10px 12px', borderBottom: `1px solid ${RULE}`, color: INK2, verticalAlign: 'top' },
  warn:  { background: '#FFF8F5', border: `1px solid #FFD0BA`, borderRadius: 8, padding: '16px 20px', margin: '0 0 16px', fontSize: 14, lineHeight: 1.7, color: INK2 },
  a:     { color: ACC, textDecoration: 'none', fontWeight: 500 },
};

const PLANS = [
  ['Trial',        '€0',      '14 days', '1', '1', '1 country',  'Full match refresh, no email alerts'],
  ['Free',         '€0',      'Ongoing', '1', '1', '1 country',  'Static matches only, no refresh'],
  ['Starter',      '€19/mo',  'Monthly', '1', '1', '1 country',  'Full refresh + email digests'],
  ['Professional', '€44/mo',  'Monthly', '3', '10','All EU',     'Full refresh, email, API access, Sales Portal'],
  ['Scale',        '€69/mo',  'Monthly', '10','20','All EU',     'All features including CSV export'],
];

export default function TermsOfService() {
  return (
    <LegalPage title="Terms of service" lastUpdated="9 May 2026">

      <p style={s.lead}>
        Please read these Terms of Service ("<strong>Terms</strong>") carefully before using Jyto.
        By creating an account or using the service, you agree to be bound by these Terms on behalf of
        yourself and, where applicable, the company you represent. If you do not agree, do not use the service.
      </p>

      <h2 style={s.h2top}>1. Who we are</h2>
      <p style={s.p}>
        {COMPANY_NAME} ("<strong>Jyto</strong>", "<strong>we</strong>", "<strong>us</strong>") provides
        a B2B SaaS platform that uses AI-assisted semantic matching to surface relevant public procurement
        tenders from the EU's Tenders Electronic Daily (TED) database for registered companies.
      </p>

      <h2 style={s.h2}>2. Eligibility</h2>
      <p style={s.p}>
        To use Jyto you must be at least 18 years old and either be acting on your own behalf as a
        sole trader or be duly authorised to accept these Terms on behalf of a company or other legal entity.
        By registering, you confirm that you meet these requirements.
      </p>

      <h2 style={s.h2}>3. Account registration and security</h2>
      <ul style={s.ul}>
        <li style={s.li}>You must provide accurate and complete information when creating your account</li>
        <li style={s.li}>You are responsible for maintaining the confidentiality of your login credentials</li>
        <li style={s.li}>You are responsible for all activity that occurs under your account</li>
        <li style={s.li}>Notify us immediately at <a href={`mailto:${LEGAL_EMAIL}`} style={s.a}>{LEGAL_EMAIL}</a> if you suspect unauthorised access</li>
        <li style={s.li}>You may not share your account credentials with persons outside your team or organisation</li>
      </ul>

      <h2 style={s.h2}>4. Subscription plans and billing</h2>

      <h3 style={s.h3}>Available plans</h3>
      <table style={s.table}>
        <thead>
          <tr>
            <th style={s.th}>Plan</th>
            <th style={s.th}>Price</th>
            <th style={s.th}>Term</th>
            <th style={s.th}>Profiles</th>
            <th style={s.th}>Users</th>
            <th style={s.th}>Geography</th>
            <th style={s.th}>Notable features</th>
          </tr>
        </thead>
        <tbody>
          {PLANS.map(([plan, price, term, profiles, users, geo, features]) => (
            <tr key={plan}>
              <td style={s.td}><strong>{plan}</strong></td>
              <td style={s.td}>{price}</td>
              <td style={s.td}>{term}</td>
              <td style={s.td}>{profiles}</td>
              <td style={s.td}>{users}</td>
              <td style={s.td}>{geo}</td>
              <td style={s.td}>{features}</td>
            </tr>
          ))}
        </tbody>
      </table>
      <p style={s.p}>
        All prices are exclusive of VAT. VAT will be added where applicable under EU rules.
      </p>

      <h3 style={s.h3}>Billing and renewal</h3>
      <ul style={s.ul}>
        <li style={s.li}>Paid subscriptions are billed monthly in advance via Stripe</li>
        <li style={s.li}>Subscriptions renew automatically unless cancelled before the renewal date</li>
        <li style={s.li}>You authorise us to charge your payment method on each renewal date</li>
      </ul>

      <h3 style={s.h3}>Cancellation and refunds</h3>
      <ul style={s.ul}>
        <li style={s.li}>You may cancel your subscription at any time through your account settings or by contacting support</li>
        <li style={s.li}>Access continues until the end of the current billing period — there is no partial-month refund by default</li>
        <li style={s.li}>Refunds at our discretion may be granted in exceptional circumstances (e.g. technical unavailability)</li>
      </ul>

      <h3 style={s.h3}>Price changes</h3>
      <p style={s.p}>
        We will give at least 30 days' notice of any price change before it affects your next renewal.
        Continued use of the service after the effective date constitutes acceptance.
      </p>

      <h2 style={s.h2}>5. Acceptable use</h2>
      <p style={s.p}>You agree not to:</p>
      <ul style={s.ul}>
        <li style={s.li}>Use Jyto for any unlawful purpose or in violation of applicable law</li>
        <li style={s.li}>Attempt to reverse-engineer, decompile, or extract the source code of the platform</li>
        <li style={s.li}>Scrape, crawl, or use automated tools to extract data from Jyto beyond permitted API access</li>
        <li style={s.li}>Resell, sublicense, or commercially redistribute Jyto data or match results without prior written consent</li>
        <li style={s.li}>Upload content that is false, misleading, or designed to manipulate matching results</li>
        <li style={s.li}>Attempt to gain unauthorised access to other accounts or backend systems</li>
        <li style={s.li}>Introduce malware, viruses, or disruptive code</li>
        <li style={s.li}>Use the service in a way that imposes an unreasonable load on our infrastructure</li>
      </ul>
      <p style={s.p}>
        We reserve the right to suspend or terminate accounts that violate these provisions without refund.
      </p>

      <h2 style={s.h2}>6. Nature of the service — important disclaimers</h2>

      <div style={s.warn}>
        <strong>AI match verdicts are for informational purposes only.</strong> Verdicts (Great / Good / Maybe)
        are automated suggestions based on semantic similarity between your company profile and public tender
        notices. They do not constitute legal advice, procurement advice, or any guarantee of eligibility,
        relevance, or success in any procurement process.
      </div>

      <div style={s.warn}>
        <strong>Tender data comes from TED.</strong> Jyto sources tender notices from the EU's
        Tenders Electronic Daily (TED) database. We make no warranty as to the accuracy, completeness,
        or timeliness of this data. Always verify tender details directly against the official TED notice
        and any associated procurement documents before submitting a bid.
      </div>

      <p style={s.p}>
        You remain solely responsible for all procurement decisions, bid submissions, and compliance with
        the rules of any specific procurement process. Jyto is a discovery and prioritisation tool,
        not a substitute for professional procurement advice.
      </p>

      <h2 style={s.h2}>7. Company profile accuracy</h2>
      <p style={s.p}>
        The quality of match results depends on the accuracy and completeness of your company profile.
        You are responsible for keeping your profile information accurate and up to date. Deliberately
        misleading profile information may result in account suspension.
      </p>

      <h2 style={s.h2}>8. Intellectual property</h2>
      <ul style={s.ul}>
        <li style={s.li}><strong>Jyto IP:</strong> all software, algorithms, designs, branding, and platform content belong to Jyto B.V. or its licensors. Nothing in these Terms grants you ownership or a licence beyond what is needed to use the service.</li>
        <li style={s.li}><strong>Your content:</strong> you retain ownership of your company profile data. By providing it, you grant Jyto a limited, non-exclusive licence to process it solely for the purpose of operating and improving the matching service.</li>
        <li style={s.li}><strong>TED data:</strong> tender notices are published by the Publications Office of the EU under open data terms. Jyto does not claim ownership of tender content.</li>
      </ul>

      <h2 style={s.h2}>9. Limitation of liability</h2>
      <p style={s.p}>
        To the fullest extent permitted by applicable law:
      </p>
      <ul style={s.ul}>
        <li style={s.li}>The service is provided "<strong>as is</strong>" without warranties of any kind, express or implied, including fitness for a particular procurement purpose</li>
        <li style={s.li}>Jyto is not liable for missed tender deadlines, incorrect or incomplete match results, or any procurement decisions made on the basis of our service</li>
        <li style={s.li}>Our total liability to you for any claim arising under these Terms is limited to the subscription fees you paid to Jyto in the three calendar months preceding the claim</li>
        <li style={s.li}>We are not liable for indirect, incidental, special, or consequential damages, including lost revenue or loss of business opportunity</li>
      </ul>
      <p style={s.p}>
        Nothing in these Terms limits our liability for death or personal injury caused by our negligence,
        fraud, or any other liability that cannot be excluded by law.
      </p>

      <h2 style={s.h2}>10. Indemnification</h2>
      <p style={s.p}>
        You agree to indemnify and hold harmless Jyto, its officers, employees, and agents from any
        claims, damages, or expenses (including reasonable legal fees) arising from your use of the service
        in violation of these Terms or applicable law.
      </p>

      <h2 style={s.h2}>11. Term and termination</h2>
      <ul style={s.ul}>
        <li style={s.li}>These Terms apply for as long as you maintain an account with Jyto</li>
        <li style={s.li}>You may delete your account at any time through your account settings</li>
        <li style={s.li}>We may suspend or terminate your account for material breach of these Terms, non-payment, or conduct that harms other users or the integrity of the service</li>
        <li style={s.li}>On termination, your data is handled as described in the <Link to="/privacy" style={s.a}>Privacy Policy</Link> — account data is deleted 30 days after account closure</li>
      </ul>

      <h2 style={s.h2}>12. Governing law and dispute resolution</h2>
      <p style={s.p}>
        These Terms are governed by the laws of the Netherlands (to be confirmed upon incorporation).
        Any dispute that cannot be resolved amicably shall be subject to the exclusive jurisdiction of
        the competent courts of [City, Netherlands — to be confirmed upon incorporation].
      </p>
      <p style={s.p}>
        If you are a consumer within the EU, you may also use the European Commission's Online Dispute
        Resolution platform at <strong>ec.europa.eu/odr</strong>.
      </p>

      <h2 style={s.h2}>13. Changes to these Terms</h2>
      <p style={s.p}>
        We may update these Terms from time to time. We will give at least 14 days' notice of material
        changes by email or by a prominent in-platform notice. Continued use after the effective date
        constitutes acceptance of the revised Terms.
      </p>

      <h2 style={s.h2}>14. Contact</h2>
      <p style={s.p}>
        For questions about these Terms, contact us at{' '}
        <a href={`mailto:${LEGAL_EMAIL}`} style={s.a}>{LEGAL_EMAIL}</a>.
      </p>

    </LegalPage>
  );
}
